/**
 * React Query mutation hook for transitioning a component rule's
 * visibility.
 *
 * Mirrors {@link useUpdateComponentRuleStatus} — the backend exposes a
 * dedicated `PATCH /claimflow/component-rules/{id}/visibility` endpoint
 * (separate from the general `PUT` update) because the transition is
 * admin-gated and returns its own 403 (`VISIBILITY_FORBIDDEN`). Keeping
 * the client call on a dedicated hook mirrors the server contract and
 * lets pages chain it after a content-only PUT.
 */

import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';

import {
  componentRulesService,
  type ComponentRuleDTO,
} from '../../api/componentRulesService';
import { componentRulesKeys } from './queryKeys';

/** Variables accepted by `useUpdateComponentRuleVisibility().mutate(...)`. */
export interface UpdateComponentRuleVisibilityVariables {
  id: string;
  visibility: 'public' | 'private';
  componentId?: string;
}

/** Mutation hook for `PATCH /claimflow/component-rules/{id}/visibility`. */
export function useUpdateComponentRuleVisibility(): UseMutationResult<
  ComponentRuleDTO,
  Error,
  UpdateComponentRuleVisibilityVariables
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, visibility }: UpdateComponentRuleVisibilityVariables) =>
      componentRulesService.updateRuleVisibility(id, visibility),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: componentRulesKeys.detail(variables.id),
      });
      void queryClient.invalidateQueries({
        queryKey: componentRulesKeys.lists(),
      });
      if (variables.componentId !== undefined) {
        void queryClient.invalidateQueries({
          queryKey: componentRulesKeys.list(variables.componentId),
        });
      }
    },
  });
}
